import type { FilterRule } from './filterRuleTypes';
import type { TextFilterOp } from './operators';

export function normalizeStr(s: string | null | undefined): string {
  return (s ?? '').toLowerCase().trim();
}

export function matchText(value: string | null | undefined, rule: Extract<FilterRule, { type: 'text' }>): boolean {
  const haystack = normalizeStr(value);

  if (rule.selectedValues.length > 0) {
    const raw = (value ?? '').toLowerCase();
    if (!rule.selectedValues.some((v) => v.toLowerCase() === raw)) return false;
  }

  if (rule.op === 'none') return true;

  const needle = normalizeStr(rule.value);
  const criteriaActive = rule.op === 'isEmpty' || rule.op === 'isNotEmpty' || needle !== '';
  if (!criteriaActive) return true;

  switch (rule.op) {
    case 'contains':
      return haystack.includes(needle);
    case 'notContains':
      return !haystack.includes(needle);
    case 'equals':
      return haystack === needle;
    case 'startsWith':
      return haystack.startsWith(needle);
    case 'isEmpty':
      return haystack === '';
    case 'isNotEmpty':
      return haystack !== '';
  }
}

export function matchNumber(
  raw: number | null | undefined,
  rule: Extract<FilterRule, { type: 'number' }>
): boolean {
  if (rule.op === 'none') return true;
  if (rule.op === 'isEmpty') return raw == null;
  if (rule.op === 'isNotEmpty') return raw != null;
  if (raw == null) return false;

  const v = rule.value;
  const v2 = rule.value2 ?? 0;

  switch (rule.op) {
    case 'eq':
      return raw === v;
    case 'neq':
      return raw !== v;
    case 'gt':
      return raw > v;
    case 'lt':
      return raw < v;
    case 'gte':
      return raw >= v;
    case 'lte':
      return raw <= v;
    case 'between':
      return raw >= v && raw <= v2;
  }
}

export function matchDate(
  raw: string | null | undefined,
  rule: Extract<FilterRule, { type: 'date' }>
): boolean {
  if (rule.op === 'none') return true;
  if (rule.op === 'isEmpty') return !raw;
  if (rule.op === 'isNotEmpty') return !!raw;
  if (!raw) return false;

  const d = raw.slice(0, 10);
  const v = rule.value.slice(0, 10);
  const v2 = (rule.value2 ?? '').slice(0, 10);

  switch (rule.op) {
    case 'equals':
      return d === v;
    case 'before':
      return d < v;
    case 'after':
      return d > v;
    case 'between':
      return d >= v && d <= v2;
  }
}

export function readerOrContractorNamesMatchText(
  names: string[],
  rule: Extract<FilterRule, { type: 'multiPerson' }>
): boolean {
  const op = rule.op ?? 'none';
  if (op === 'isEmpty') return names.length === 0;
  if (op === 'isNotEmpty') return names.length > 0;
  if (op === 'none') return true;

  const needle = normalizeStr(rule.value);
  if (needle === '') return true;

  const haystacks = names.map((n) => normalizeStr(n));
  const any = (pred: (h: string) => boolean) => haystacks.some(pred);

  switch (op) {
    case 'contains':
      return any((h) => h.includes(needle));
    case 'notContains':
      return haystacks.length === 0 || haystacks.every((h) => !h.includes(needle));
    case 'equals':
      return any((h) => h === needle);
    case 'startsWith':
      return any((h) => h.startsWith(needle));
    default:
      return true;
  }
}

export function normalizeMultiPersonRule(rule: FilterRule): FilterRule {
  if (rule.type !== 'multiPerson') return rule;
  const r = rule as { names?: string[]; op?: TextFilterOp; value?: string };
  return {
    type: 'multiPerson',
    names: Array.isArray(r.names) ? r.names : [],
    op: r.op ?? 'none',
    value: typeof r.value === 'string' ? r.value : '',
  };
}
